import { useEffect, useState } from 'react';
import { useDetail } from '../hooks/useDetail.js';
import { sanitizeHtml } from '../utils/sanitizeHtml.js';
import { assetUrl } from '../assetUrl.js';

/**
 * The centered modal dialog that shows the details behind a search result.
 *
 * Replaces the right-side sliding drawer with a large, centered modal dialog
 * matching the Crizac production reference interface.
 *
 * The list stays on Typesense; this is the only place the MongoDB-backed detail
 * API is called, and only while something is open (`id != null`). Three states:
 * loading, error (with Retry), and the record.
 *
 * The API returns a SAFE ALLOWLIST DTO — never the raw MongoDB document, never
 * contract / commission / routing / access-control fields, never a `_id`. This
 * component renders that DTO into conditional sections; a field the DTO omitted
 * (because it was empty) simply produces no section.
 *
 * `kind` is `'programs'` or `'universities'`. `id` is the id straight off the
 * Typesense hit. `hit` (universities only) is the search row, used for the few
 * admission signals that are derived in the search index, not the document.
 */

/* ---------- small render helpers ---------- */

function stripTags(v) {
  return String(v ?? '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&').replace(/\s+/g, ' ').trim();
}

function initials(name) {
  return (name ?? '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

/** A definition row; renders nothing when empty. */
function Row({ label, children }) {
  if (children == null || children === '' || (Array.isArray(children) && children.length === 0)) return null;
  return (
    <div className="detail-field">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/**
 * A titled section. `has` is the explicit "does this section have data" gate —
 * it must be passed so an empty section never renders a bare heading (§18).
 */
function Section({ title, has, children }) {
  if (!has) return null;
  return (
    <section className="detail-section">
      <h4>{title}</h4>
      {children}
    </section>
  );
}

/** Sanitised HTML block. Renders nothing if sanitising leaves it empty. */
function Html({ html }) {
  const safe = sanitizeHtml(html);
  if (!safe) return null;
  // eslint-disable-next-line react/no-danger
  return <div className="detail-html" dangerouslySetInnerHTML={{ __html: safe }} />;
}

function Chips({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="detail-chips">
      {items.map((it) => <span className="detail-chip" key={it}>{it}</span>)}
    </div>
  );
}

function ExternalLink({ href, children }) {
  if (!href) return null;
  const url = /^https?:\/\//i.test(href) ? href : `https://${href}`;
  return <a href={url} target="_blank" rel="noopener noreferrer">{children ?? url}</a>;
}

/* ---------- program (course) body ---------- */

function formatMoney(amount, currency) {
  if (amount == null || amount === '') return null;
  const s = String(amount).trim();
  if (!s || s === '0' || s === '0.00') return null;
  const sym = currency?.symbol || '';
  const code = currency?.code || '';
  if (/^[£$€₹]/.test(s) || /\b(GBP|USD|EUR|AUD|CAD|INR)\b/i.test(s)) return s;
  const n = Number(s);
  const formatted = Number.isFinite(n) ? n.toLocaleString() : s;
  if (sym) return `${sym}${formatted}${code && sym !== code ? ` ${code}` : ''}`;
  return code ? `${code} ${formatted}` : formatted;
}

function formatAppFee(amount, currency) {
  if (amount == null || amount === '') return null;
  const s = String(amount).trim();
  if (s === '0' || s === '0.00' || s.toLowerCase() === 'free') return 'Free to apply';
  return formatMoney(s, currency) || s;
}

function AcademicRequirementsTable({ entries }) {
  if (!entries || entries.length === 0) return null;
  const [activeNote, setActiveNote] = useState(null);

  return (
    <div className="detail-table-wrap">
      <table className="detail-table">
        <thead>
          <tr>
            <th>Country</th>
            <th>Qualification / Board</th>
            <th>Level</th>
            <th>Grading Scale</th>
            <th>Minimum Requirement</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry, idx) => {
            return (
              <tr key={`${entry.country}-${entry.qualification}-${entry.scale}-${idx}`}>
                <td className="detail-cell-country">{entry.country || 'General / International'}</td>
                <td className="detail-cell-qual"><strong>{entry.qualification || '—'}</strong></td>
                <td className="detail-cell-level">{entry.educationLevel || '—'}</td>
                <td className="detail-cell-scale">
                  {entry.scale ? (
                    <span className="detail-scale-badge">{entry.scale}</span>
                  ) : '—'}
                </td>
                <td className="detail-cell-score">
                  {(() => {
                    const rawScore = entry.score || entry.value;
                    const isNotesText = !rawScore || /see\s*notes|refer\s*notes|check\s*notes/i.test(rawScore);

                    if (isNotesText) {
                      return entry.notes ? (
                        <button
                          type="button"
                          className="detail-note-pill-btn detail-note-readmore"
                          onClick={() => setActiveNote(entry)}
                          title="View requirement details in notes"
                        >
                          See notes ↗
                        </button>
                      ) : (
                        <span className="detail-score-val detail-empty-val">—</span>
                      );
                    }

                    if (entry.notes) {
                      return (
                        <button
                          type="button"
                          className="detail-note-pill-btn detail-note-readmore"
                          onClick={() => setActiveNote(entry)}
                          title="Click to view notes"
                        >
                          {rawScore} · See notes ↗
                        </button>
                      );
                    }

                    return <span className="detail-score-val">{rawScore}</span>;
                  })()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {activeNote && (
        <div className="detail-note-popout-overlay" onClick={() => setActiveNote(null)}>
          <div
            className="detail-note-popout"
            role="dialog"
            aria-modal="true"
            aria-label="Requirement Notes"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="detail-note-popout-head">
              <div className="detail-note-popout-title">
                <span className="detail-note-kicker">{activeNote.country || 'Requirement Detail'}</span>
                <h4>{activeNote.qualification || 'Academic Requirement Notes'}</h4>
              </div>
              <button
                type="button"
                className="detail-note-popout-close"
                onClick={() => setActiveNote(null)}
                aria-label="Close notes"
              >
                ×
              </button>
            </div>
            <div className="detail-note-popout-body">
              <div className="detail-note-popout-meta">
                {activeNote.educationLevel && (
                  <span className="detail-meta-tag">Level: <strong>{activeNote.educationLevel}</strong></span>
                )}
                {activeNote.scale && (
                  <span className="detail-meta-tag">Scale: <strong>{activeNote.scale}</strong></span>
                )}
                {(activeNote.score || activeNote.value) && (
                  <span className="detail-meta-tag">Requirement: <strong>{activeNote.score || activeNote.value}</strong></span>
                )}
              </div>
              <div className="detail-note-popout-text">
                <h5>Notes & Evaluation Guidance</h5>
                <p>{activeNote.notes}</p>
              </div>
            </div>
            <div className="detail-note-popout-footer">
              <button
                type="button"
                className="detail-note-popout-done"
                onClick={() => setActiveNote(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CourseBody({ payload }) {
  const c = payload.course ?? {};
  const overview = c.university || c.country || c.level || c.duration
    || c.tuitionFees || c.applicationFees || c.location || c.atasRequired || c.courseUrl
    || c.universityRating != null || c.universityRanking;

  const tuitionDisplay = formatMoney(c.tuitionFees, c.currency) || c.tuitionFees;
  const appFeeDisplay = formatAppFee(c.applicationFees, c.currency);

  const hasCosts = Boolean(
    c.tuitionFees || c.applicationFees || c.costOfLiving || c.universityFees || sanitizeHtml(c.scholarship)
  );

  const academicEntries = Array.isArray(c.academicRequirements?.entries)
    ? c.academicRequirements.entries
    : [];
  const hasAcademic = Boolean(academicEntries.length || sanitizeHtml(c.entryRequirements));

  const sections = Array.isArray(c.sections) ? c.sections : [];

  return (
    <div className="modal-pane-content">
      <Section title="Overview" has={overview}>
        <dl className="detail-fields">
          <Row label="University">{c.university}</Row>
          <Row label="University rating">{c.universityRating != null ? `${c.universityRating} / 5` : null}</Row>
          <Row label="University ranking">{c.universityRanking}</Row>
          <Row label="Country">{c.country}</Row>
          <Row label="Level">{c.level}</Row>
          <Row label="Duration">{c.duration}</Row>
          <Row label="Tuition fees">{tuitionDisplay}</Row>
          <Row label="Application fee">{appFeeDisplay}</Row>
          <Row label="Location">{c.location}</Row>
          <Row label="ATAS required">{c.atasRequired ? 'Yes' : null}</Row>
          <Row label="Course page"><ExternalLink href={c.courseUrl} /></Row>
        </dl>
      </Section>

      <Section title="Costs" has={hasCosts}>
        <dl className="detail-fields">
          <Row label="Tuition fees">{tuitionDisplay}</Row>
          <Row label="Application fee">{appFeeDisplay}</Row>
          <Row label="Cost of living">{c.costOfLiving}</Row>
          <Row label="Tuition fee notes">{c.universityFees}</Row>
        </dl>
        {c.scholarship && (
          <div className="detail-scholarship-sub">
            <h5>Scholarship Information</h5>
            <Html html={c.scholarship} />
          </div>
        )}
      </Section>

      <Section title="Academic Entry Requirements" has={hasAcademic}>
        <AcademicRequirementsTable entries={academicEntries} />
        {c.entryRequirements && <Html html={c.entryRequirements} />}
      </Section>

      <Section title="Description" has={sanitizeHtml(c.description)}><Html html={c.description} /></Section>
      {sections.map((s) => (
        <Section key={s.title} title={s.title} has={sanitizeHtml(s.html)}><Html html={s.html} /></Section>
      ))}
    </div>
  );
}

/* ---------- university body ---------- */

function mapLink(loc) {
  if (!loc) return null;
  return `https://www.openstreetmap.org/?mlat=${loc.lat}&mlon=${loc.lng}#map=14/${loc.lat}/${loc.lng}`;
}

function UniversityBody({ payload, hit }) {
  const u = payload.university ?? {};
  const money = [u.currency?.symbol || u.currency?.code, u.fees].filter(Boolean).join(' ');

  // Admission signals derived in the search index (not the document). Only the
  // positive signals are shown; "interview" is flagged unverified because the
  // authoritative "no interview" state is indistinguishable from "no data".
  const admissions = [
    hit?.moiAccepted ? 'Accepts Medium of Instruction (MOI) in place of an English test' : null,
    hit?.gapYearsMax != null ? `Academic gap accepted: up to ${hit.gapYearsMax} year${hit.gapYearsMax === 1 ? '' : 's'}` : null,
    hit?.interviewRequired ? 'Credibility interview required (per Crizac records — unverified)' : null,
  ].filter(Boolean);

  const hasOverview = u.country || u.city || u.foundingYear || u.institutionType
    || u.rating != null || u.ranking || hit?.isPathway || u.website;

  return (
    <div className="modal-pane-content">
      <div className="detail-uni-header-summary">
        <div className="detail-uni-avatar-badge">
          {u.media?.logo ? (
            <img src={assetUrl(u.media.logo)} alt={u.name} className="detail-avatar-img" />
          ) : (
            <span className="detail-avatar-init">{initials(u.name)}</span>
          )}
        </div>
        <div className="detail-uni-header-meta">
          <h3 className="detail-uni-name-heading">{u.name}</h3>
          <p className="detail-uni-address-line">{u.address || [u.city, u.country].filter(Boolean).join(', ')}</p>
        </div>
      </div>

      <Section title="Overview" has={hasOverview}>
        <dl className="detail-fields">
          <Row label="Country">{u.country}</Row>
          <Row label="City">{u.city}</Row>
          <Row label="Founded">{u.foundingYear}</Row>
          <Row label="Institution type">{u.institutionType}</Row>
          <Row label="Rating">{u.rating != null ? `${u.rating} / 5` : null}</Row>
          <Row label="Ranking">{u.ranking}</Row>
          <Row label="Pathway provider">{hit?.isPathway ? 'Yes' : null}</Row>
          <Row label="Website"><ExternalLink href={u.website} /></Row>
        </dl>
      </Section>

      <Section title="Location & campuses" has={u.address || u.campuses?.length || u.location}>
        <dl className="detail-fields">
          <Row label="Address">{u.address}</Row>
        </dl>
        {u.campuses?.length ? (
          <ul className="detail-campuses">
            {u.campuses.map((cmp) => <li key={cmp.name}>{cmp.name}</li>)}
          </ul>
        ) : null}
        {u.location ? (
          <p className="detail-maplink">
            <ExternalLink href={mapLink(u.location)}>View on map</ExternalLink>
          </p>
        ) : null}
      </Section>

      <Section title="About" has={sanitizeHtml(u.about)}><Html html={u.about} /></Section>
      <Section title="Key facts" has={sanitizeHtml(u.keyFacts)}><Html html={u.keyFacts} /></Section>

      <Section title="Course types" has={u.courseTypes?.length}><Chips items={u.courseTypes} /></Section>
      <Section title="Intakes" has={u.intakes?.length}><Chips items={u.intakes} /></Section>

      <Section title="Fees & cost of living" has={u.fees || u.costOfLiving}>
        <dl className="detail-fields">
          <Row label="Tuition fees">{money || u.fees}</Row>
          <Row label="Cost of living">{u.costOfLiving}</Row>
        </dl>
        {u.fees && (
          <p className="detail-fee-note">**Tuition fees vary depending on the course.**</p>
        )}
      </Section>

      <Section title="Entry requirements" has={sanitizeHtml(u.entryRequirements)}><Html html={u.entryRequirements} /></Section>
      <Section title="English requirements" has={sanitizeHtml(u.englishRequirements)}><Html html={u.englishRequirements} /></Section>

      <Section title="Admission notes" has={admissions.length}>
        <ul className="detail-notes">{admissions.map((a) => <li key={a}>{a}</li>)}</ul>
      </Section>

      <Section title="Scholarships" has={u.scholarshipAvailable || sanitizeHtml(u.scholarship)}>
        {u.scholarshipAvailable ? <p className="detail-flagline">Scholarships available</p> : null}
        <Html html={u.scholarship} />
      </Section>

      <Section title="Application process" has={sanitizeHtml(u.applicationProcess) || u.sopRequired}>
        <Html html={u.applicationProcess} />
        {u.sopRequired ? (
          <dl className="detail-fields"><Row label="SOP required">{u.sopRequired}</Row></dl>
        ) : null}
      </Section>

      <Section title="Accommodation" has={sanitizeHtml(u.accommodation)}><Html html={u.accommodation} /></Section>

      <Section title="Media" has={u.media?.banner || u.media?.logo || u.media?.campus || u.media?.youtube}>
        {u.media?.banner ? <img className="detail-media-banner" src={assetUrl(u.media.banner)} alt={`${u.name} banner`} loading="lazy" /> : null}
        {u.media?.logo ? <img className="detail-media-logo" src={assetUrl(u.media.logo)} alt={`${u.name} logo`} loading="lazy" /> : null}
        {u.media?.campus ? <img className="detail-media-banner" src={assetUrl(u.media.campus)} alt={`${u.name} campus`} loading="lazy" /> : null}
        {u.media?.youtube ? (
          <p><ExternalLink href={u.media.youtube.replace('/embed/', '/watch?v=')}>Watch introduction video</ExternalLink></p>
        ) : null}
      </Section>
    </div>
  );
}

/**
 * The IELTS Requirement tab.
 *
 * Live-verified behaviour (see the IELTS research report): the tab itself is
 * NEVER conditionally hidden — a university with no IELTS entries still shows
 * the tab and the "IELTS Information" heading, just with a blank body. So this
 * component is not gated behind a `has` check the way every other section is;
 * `payload.university.ielts` is rendered when present and simply produces no
 * rows when it isn't, matching the live empty state exactly rather than
 * inventing a "Not available" message.
 */
function IeltsBody({ payload }) {
  const entries = payload.university?.ielts ?? [];
  return (
    <div className="modal-pane-content">
      <section className="detail-section detail-ielts">
        <h4>IELTS Information</h4>
        {entries.map((row) => (
          <div className="detail-ielts-row" key={row.level}>
            <div className="detail-ielts-level">{row.level}</div>
            <div className="detail-ielts-band">Requires {row.total} Bands</div>
            {row.minBand != null ? (
              <div className="detail-ielts-min">with no less than {row.minBand} Bands</div>
            ) : null}
          </div>
        ))}
        {/* Live-verified: the disclaimer itself is conditional on having at least
            one row too — a university with zero entries shows a bare "IELTS
            Information" heading and nothing else, not even the disclaimer. */}
        {entries.length ? (
          <p className="detail-ielts-disclaimer">
            ** Please note that IELTS requirements can vary from course to course. **
          </p>
        ) : null}
      </section>
    </div>
  );
}

/* ---------- shell ---------- */

/**
 * The two tabs the live reference's University Details popup has: "University
 * Information" and "IELTS Requirement" (see the IELTS research report).
 */
const TAB_ICONS = {
  program: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
      <path d="M6 12v5c3 3 9 3 12 0v-5" />
    </svg>
  ),
  university: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 21h18M3 10h18M5 10v11M19 10v11M9 10v11M15 10v11M12 2L2 7h20z" />
    </svg>
  ),
  info: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
  ielts: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="8" r="7" />
      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
    </svg>
  ),
};

const UNIVERSITY_TABS = [
  { key: 'info', label: 'University Information' },
  { key: 'ielts', label: 'IELTS Requirement' },
];

/**
 * Program Details gets the same tabbed layout: the course itself, plus a
 * "University" tab carrying the full university DTO the API now joins onto the
 * course response (`payload.university`). The University tab only appears when
 * that join succeeded — otherwise the course renders untabbed as before.
 */
const PROGRAM_TABS = [
  { key: 'program', label: 'Program Details' },
  { key: 'university', label: 'University' },
];

export function DetailDrawer({ kind, id, hit, onClose }) {
  const { data, loading, error, reload } = useDetail(kind, id);
  const open = id != null;
  const isUni = kind === 'universities';
  const [tab, setTab] = useState(isUni ? 'info' : 'program');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock the page behind the modal while it is open. Without this a wheel
  // gesture that starts over the dimmed backdrop — or continues past the end of
  // the modal body — scrolls the search results underneath instead, which reads
  // as "the popup won't scroll".
  useEffect(() => {
    if (!open) return undefined;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = overflow; };
  }, [open]);

  // A new record opened — reset to its first tab rather than carrying over
  // whichever tab was selected for the last one.
  useEffect(() => { setTab(isUni ? 'info' : 'program'); }, [isUni, id]);

  if (!open) return null;

  const title = isUni
    ? (stripTags(data?.university?.name) || stripTags(data?.name) || 'University Details')
    : (stripTags(data?.course?.name) || stripTags(data?.name) || stripTags(data?.university?.name) || 'Program Details');

  return (
    <div className="detail-overlay" onClick={onClose}>
      <aside
        className="detail-drawer detail-modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={`${isUni ? 'University' : 'Program'} details`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="detail-head">
          <div className="detail-head-info">
            <div className="detail-kicker">{isUni ? 'University details' : 'Program details'}</div>
            <h3 id="detailTitle">{loading && !data ? 'Loading…' : title}</h3>
          </div>
          <button
            type="button"
            className="detail-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {(() => {
          // Which tab set applies, and whether the sidebar shows at all. A
          // program with no joined university stays untabbed exactly as before.
          const hasProgramTabs = !isUni && !!data?.university;
          const tabs = isUni ? UNIVERSITY_TABS : PROGRAM_TABS;
          const showTabs = !!data && (isUni || hasProgramTabs);

          return (
            <div className="detail-modal-layout">
              {showTabs && (
                <div className={`detail-tabs-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
                  <div className="detail-sidebar-header">
                    <button
                      type="button"
                      className={`detail-sidebar-toggle ${sidebarCollapsed ? 'collapsed' : ''}`}
                      onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                      title={sidebarCollapsed ? 'Expand tabs' : 'Collapse tabs to icons'}
                      aria-label={sidebarCollapsed ? 'Expand tabs' : 'Collapse tabs to icons'}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          transform: sidebarCollapsed ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.2s ease',
                        }}
                      >
                        <polyline points="15 18 9 12 15 6" />
                      </svg>
                      {!sidebarCollapsed && <span className="detail-sidebar-toggle-text">INFO</span>}
                    </button>
                  </div>
                  <div className="detail-tabs" role="tablist">
                    {tabs.map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        role="tab"
                        aria-selected={tab === t.key}
                        className={`detail-tab ${tab === t.key ? 'on' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`}
                        onClick={() => setTab(t.key)}
                        title={sidebarCollapsed ? t.label : undefined}
                      >
                        <span className="detail-tab-icon">{TAB_ICONS[t.key]}</span>
                        {!sidebarCollapsed && <span className="detail-tab-label">{t.label}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="detail-body">
                {loading && !data && <p className="detail-status">Fetching the full record…</p>}

                {error && !loading && (
                  <div className="detail-error">
                    <p>{error}</p>
                    <button type="button" onClick={reload}>Try again</button>
                  </div>
                )}

                {data && !isUni && tab !== 'university' && <CourseBody payload={data} />}
                {data && !isUni && tab === 'university' && data.university && (
                  <UniversityBody payload={data} hit={undefined} />
                )}
                {data && isUni && tab === 'info' && <UniversityBody payload={data} hit={hit} />}
                {data && isUni && tab === 'ielts' && <IeltsBody payload={data} />}
              </div>
            </div>
          );
        })()}
      </aside>
    </div>
  );
}
